<script lang="ts">
export default {
  name: 'CDataTable',
}

interface Props extends /* @vue-ignore */ DataTableProps {
  columns: DataTableColumns<any>
  pagination?: false | PaginationProps
  maxHeight?: string | number
}
</script>

<script lang="ts" setup>
/* DEPRECATED: Use CTable instead */
import { computed, ref } from 'vue'
import { merge } from 'lodash-es'
import { isPitcherOrIosDevice } from '@lib/util/userAgents'
import type { PaginationProps, DataTableProps, DataTableColumns } from 'naive-ui'
import { NDataTable } from 'naive-ui'
import type { FilterState, ColumnKey, SortOrder, TableBaseColumn } from 'naive-ui/lib/data-table/src/interface'

import type { ScrollTo } from 'naive-ui/es/scrollbar/src/Scrollbar'
import { useCDataTableEdit } from '@lib/components/CDataTable/CDataTableEdit.use'
import useThemeVars from '@lib/composables/useThemeVars'

const themeVars = useThemeVars()

const props = defineProps<Props>()

const dataTableRef = ref<InstanceType<typeof NDataTable> | null>(null)

const defaultPagination = {
  size: isPitcherOrIosDevice ? 'large' : 'medium',
  page: 1,
  pageSize: 10,
  showSizePicker: true,
  pageSizes: [10, 20, 50, 100],
} as PaginationProps

const pagination = computed(() => merge(props.pagination, { ...defaultPagination, ...props.pagination }))
const paginationBaseSelectionPadding = computed(() => (pagination.value?.size === 'large' ? `6px` : 0))

const {
  editCell,
  isEmptyEditCell,
  editCellTableOverflow,
  editCellTableFixedTdThPosition,
  onInlineInputUpdate,
  onInlineInputCancel,
  onInlineInputEdit,
  getInputState,
} = useCDataTableEdit(dataTableRef)

const columns = Array.isArray(props.columns)
  ? (props.columns as TableBaseColumn<any>[]).reduce((acc, curr, index: number) => {
      acc = [
        ...acc,
        {
          width: curr.type === 'selection' ? 40 : 200,
          fixed: curr.type === 'selection' ? 'left' : undefined,
          minWidth: curr.type === 'selection' ? 40 : 100,
          key: curr.key,
        },
      ]

      return acc
    }, [] as TableBaseColumn<any>[])
  : []

const scrollX = props.columns?.reduce((acc, column) => {
  if (column.width !== undefined) {
    const width = column.width as number
    return acc + width
  }

  return acc
}, 0)

const rowKey = (row: any) => row.id

//Note: Only for impact
const maxHeight = computed(() => {
  if (props.maxHeight) return props.maxHeight

  const heightOffsetWithPagination = isPitcherOrIosDevice ? 398 : 378
  const paginationSize = isPitcherOrIosDevice ? 76 : 56

  const totalHeightOffset = !props.pagination ? heightOffsetWithPagination - paginationSize : heightOffsetWithPagination

  return `calc(100vh - ${totalHeightOffset}px)`
})

const defaultProps: DataTableProps = {
  columns,
  scrollX,
  rowKey,
  maxHeight: maxHeight.value,
  bordered: false,
  data: [],
  renderCell: (value: string | number) => value ?? '-',
  virtualScroll: false,
  pagination: pagination.value,
}

const mergedProps: DataTableProps = merge(defaultProps, props)

defineExpose({
  scrollTo: dataTableRef.value?.scrollTo as ScrollTo, //seems working
  clearFilters: () => dataTableRef.value?.clearFilters() as void,
  clearSorter: () => dataTableRef.value?.clearSorter() as any,
  filters: (filters: FilterState | null) => dataTableRef.value?.filters(filters) as void,
  page: (page: number) => dataTableRef.value?.page(page) as void,
  sort: (columnKey: ColumnKey, order: SortOrder) => dataTableRef.value?.sort(columnKey, order) as void,
  editCell,
  isEmptyEditCell,
  onInlineInputUpdate,
  onInlineInputCancel,
  onInlineInputEdit,
  getInputState,
})
</script>

<template>
  <NDataTable ref="dataTableRef" v-bind="mergedProps" class="c-data-table" />
</template>

<style lang="scss" scoped>
.n-data-table {
  overflow: v-bind('editCellTableOverflow');
}

:deep(.n-data-table__pagination .n-base-selection) {
  padding: v-bind('paginationBaseSelectionPadding');
}

:deep(.n-data-table__pagination) {
  background-color: v-bind('themeVars.base');
  padding: 8px;
}

:deep(.n-data-table-td) {
  padding-bottom: 0;
  padding-top: 0;
}

:deep(.n-data-table-td.n-data-table-td--fixed-left),
:deep(.n-data-table-th.n-data-table-th--sortable),
:deep(.n-data-table-th.n-data-table-th--fixed-left) {
  position: v-bind('editCellTableFixedTdThPosition');
}
</style>
